import { Temporal } from '@js-temporal/polyfill'

import type { Template, TemplateCampaign } from '../types'

/**
 * Gets all campaigns linked to a template via linkedCampaigns.
 */
export const getLinkedCampaigns = (
  template: Template,
  campaigns: TemplateCampaign[],
): TemplateCampaign[] => {
  const linked: TemplateCampaign[] = []

  if (template.linkedCampaigns?.length) {
    for (const campaignId of template.linkedCampaigns) {
      const campaign = campaigns.find((c) => c.id === campaignId)
      if (campaign) linked.push(campaign)
    }
  }

  return linked
}

/**
 * Checks if a campaign allows templates to be visible.
 * - Draft campaigns: always hidden
 * - Active/Archived: check start/end dates and showTemplatesAfterEndDate
 */
export const isCampaignVisible = (
  campaign: TemplateCampaign,
  currentTime: Temporal.ZonedDateTime,
): boolean => {
  // Draft campaigns are always hidden
  if (campaign.status === 'Draft') return false

  // Campaign hasn't started yet
  if (Temporal.ZonedDateTime.compare(campaign.startDate, currentTime) > 0) {
    return false
  }

  // Campaign has ended and showTemplatesAfterEndDate is false
  if (
    Temporal.ZonedDateTime.compare(campaign.endDate, currentTime) < 0 &&
    !campaign.showTemplatesAfterEndDate
  ) {
    return false
  }

  return true
}

/**
 * Checks if a template should be visible based on its campaign status.
 * Returns true if:
 * - Template has no linked campaigns (visible by default)
 * - ANY linked campaign allows visibility (OR logic)
 *
 * Templates can be linked to multiple campaigns. As long as at least one
 * campaign is visible, the template is shown.
 */
export const isTemplateCampaignActive = (
  template: Template,
  currentTime: Temporal.ZonedDateTime,
  campaigns: TemplateCampaign[],
): boolean => {
  const linkedCampaigns = getLinkedCampaigns(template, campaigns)

  // No linked campaigns - show by default
  if (linkedCampaigns.length === 0) return true

  // OR logic: visible if ANY linked campaign is visible
  return linkedCampaigns.some((campaign) =>
    isCampaignVisible(campaign, currentTime),
  )
}
